import { AppointmentID } from "./Appointment";
import { CarID } from "./Car";
import { CheckInAttachment, CheckInRemark, CheckInRemarkID, CheckInResult, CheckInResultID } from "./CheckIn";
import { ChecklistID } from "./Checklist";
import { COMMUNICATION_RESULT_STATUSES, DiagnoseOverviewAttachment, DiagnoseOverviewRemark, DiagnoseOverviewRemarkID } from "./CustomerCommunication";
import { DealerLocationID } from "./DealerLocation";
import { InterventionElement } from "./InterventionElement";
import { KeyLockerRemark, KeyLockerRemarkID } from "./Keylocker";
import { KioskRemark, KioskRemarkID } from "./KioskCommunication";
import { Pin, PIN_STATUSES, PIN_TYPES, WARRANTY_TYPES } from "./Pin";
import { QUESTION_RESULT_STATUSES, QuestionResultID } from "./QuestionResult";
import { Snooze, SnoozeID } from "./Snooze";
import { VehicleTyreReplacement } from "./Tyre";
import { User, UserID } from "./User";

export type InterventionID = number;

export type InterventionAttachmentID = number;

export interface Intervention {
  created_on: string;
  updated_on: string;
  id: InterventionID;
  dms_nr: string; // Intervention ID in the DMS
  title: string; // Intervention title
  description: string; // Description of what the intervention is for
  internal_note: string; // An internal note never shown to the customer
  price: number; // Relative cost of intervention that should be covered by the Customer
  ordering: number | null; // A DMS can define its own ordering that needs to be replicated in the UI. When this is null, order by the ID ascending.
  vat: number | null; // VAT
  labor_minutes: number | null; // Sum of minutes for labor elements
  dms_price_edited: boolean;
  dms_deleted: boolean; // Intervention was deleted in the DMS but we cannot delete it due to relations
  mechanic_notes: string; // Mechanic's notes
  pinned: boolean; // Whether the intervention is pinned in Claire
  snoozed: boolean; // Whether the intervention is snoozed in Claire
  is_declined_by_customer: boolean; // Whether the customer declined the intervention in Claire
  is_signed_by_customer: boolean; // The customer has explicitly seen and signed for this intervention. Whether it's agreed or declined to be fixed, is not handled by this field
  last_mechanic_name: string; // The last mechanic to update the intervention
  mechanic_fixed: boolean; // Was the intervention fixed by a Mechanic
  customer_ok: boolean; // Customer has agreed to the intervention
  is_local: boolean; // Intervention was created manually in Claire
  is_maintenance: boolean; // Intervention is of the type maintenance
  is_internal_in_dms: boolean; // Intervention is internal in the dms
  is_warranty_in_dms: boolean; // Intervention is warranty in the dms
  visible_to_customer: boolean; // Intervention is visible to the Customer
  appointment_id: AppointmentID;
  question_result_id: QuestionResultID | null; // Question result identifier if converted from Question Result
  check_in_remark_id: CheckInRemarkID | null;
  diagnose_overview_remark_id: DiagnoseOverviewRemarkID | null;
  keylocker_remark_id: KeyLockerRemarkID | null;
  kiosk_remark_id: KioskRemarkID | null;
  snooze_id: SnoozeID | null;
  check_in_result_id: CheckInResultID | null;
  dealer_location_id: DealerLocationID;
  communication_result_status?: COMMUNICATION_RESULT_STATUSES;
  checkin_attachments?: CheckInAttachment[];
  diagnose_overview_attachments?: DiagnoseOverviewAttachment[];
  pin_history?: Pin[];
  snooze_history?: Snooze[];
  check_in_result?: CheckInResult;
  check_in_remark?: CheckInRemark;
  diagnose_overview_remark?: DiagnoseOverviewRemark;
  keylocker_remark?: KeyLockerRemark; // Customer remark during keylocker check-in that has been converted to this intervention.
  kiosk_remark?: KioskRemark; // Customer remark during kiosk check-in that has been converted to this intervention.
  snooze?: Snooze;
  car_id?: CarID;
  elements?: InterventionElement[]; // Elements of the Intervention
  pin_type_id?: PIN_TYPES; // Type of the pin
  pin_status_id?: PIN_STATUSES; // Status of the pin
  warranty_type_id?: WARRANTY_TYPES; // If pinned, and if it is warranty, then type of warranty
  question_result_status?: QUESTION_RESULT_STATUSES; // Status of the question result if converted from Question Result
  attachments?: InterventionAttachment[]; // Attachments of the Intervention
  reference_nr?: string; // The client can provide a reference nr in order to map to the ID. Used when frontend are creating an appointment and can have multiple interventions.
  pending_checklists?: InterventionPendingChecklist[]; // Checklists that were selected to be completed for this intervention
  replacements?: VehicleTyreReplacement[]; // Replacements to be mounted on the vehicle or storage
  intervention_code?: string; // The intervention code, usually defined as the menu code
  dms_wo_number?: string; // The WONr of the appointment, so this intervention can be paired to its appointment
}

export interface InterventionAttachment {
  created_on: string;
  updated_on: string;
  id: InterventionAttachmentID;
  url: string; // URL of the attachment
  type: string; // Type of the attachment
  name: string; // Name of the attachment
  visible_to_customer: boolean; // PDF is visible in customer communication
  intervention_id: InterventionID; // ID of the intervention
  user_id: UserID; // User ID of the user who added this attachment
  is_final_car_check: boolean; // Attachment was added during the final check
  appointment_id?: AppointmentID; // Appointment Identifier
  user?: User; // User who added the attachment
}

export interface InterventionPendingChecklist {
  created_on: string;
  updated_on: string;
  intervention_id: InterventionID; // Intervention Identifier
  checklist_id: ChecklistID; // Checklist Identifier
  ordering: number; // When there are multiple checklists assigned to 1 intervention, this specifies the sequential ordering to display them in.
  appointment_id?: AppointmentID; // Appointment Identifier
  checklist_name?: string; // The name of the checklist that is pending
}
